import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { useNotifications } from '../NotificationProvider';
import { GUARD_SAFE_ATTR, UNSAVED_MESSAGE, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { reportSaveError, useFieldErrors } from '../forms/_shared';
import { loadClients, type Client } from '../../services/ClientService';
import { updateProject } from '../../services/ProjectService';
import {
  EMPTY_PROJECT_FORM, changedFields, toFormValues, validateProjectForm,
  type ProjectFormErrors, type ProjectFormValues,
} from '../../utils/projectForm';
import { editProjectPath, projectBillingPath, type ProjectEditTab } from '../../utils/accountRoutes';
import { roleLevel } from '../../utils/roles';
import EmptyState from '../EmptyState';
import LoadingSpinner from '../LoadingSpinner';
import AccountBar from './AccountBar';
import AccountFormPage from './AccountFormPage';
import ProjectDetailsForm from '../project/ProjectDetailsForm';
import ProjectBillingPanel from '../project/ProjectBillingPanel';
import '../../styles/components/AccountPage.css';
import '../../styles/components/EditProjectPage.css';

const LIST_PATH = '/projects';

interface EditProjectPageProps {
  /** The project from the URL; null when the URL did not hold a usable id. */
  projectId: string | null;
  tab?: ProjectEditTab;
}

const TABS: { id: ProjectEditTab; label: string }[] = [
  { id: 'details', label: 'Details' },
  { id: 'billing', label: 'Billing' },
];

/** A page-sized message with a way back to the Projects list. */
const Notice: React.FC<{ icon: string; title: string; description: string }> = ({ icon, title, description }) => (
  <div className="wt-container fade-in ap-page">
    <AccountBar view="project-edit" />
    <EmptyState icon={icon} title={title} description={description}
      action={<Link className="epp-link" to={LIST_PATH}>Back to Projects</Link>} />
  </div>
);

/**
 * Edit project, as a page of its own at /workspace/projects/PJ-<id>/edit
 * (Details) and .../billing (Billing). It replaces the side drawer so the form
 * has a URL, survives a reload and can be linked to. Both tabs stay mounted
 * under one route component, so unsaved Details survive a look at Billing.
 */
const EditProjectPage: React.FC<EditProjectPageProps> = ({ projectId, tab = 'details' }) => {
  const navigate = useNavigate();
  const { workspaces, fetchProjectDetails, refreshWorkspaces } = useAuth();
  const { canEdit } = useRole();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();
  const savingRef = useRef(false);

  const workspace = projectId ? workspaces.find(w => w.project_id === projectId) : undefined;
  // Planner level or above, both in the account and in this project.
  const mayEdit = canEdit && !!workspace && roleLevel(workspace.role) >= roleLevel('planner');

  const [loading, setLoading] = useState(true);
  const [partialLoad, setPartialLoad] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientName, setClientName] = useState('');
  const [initial, setInitial] = useState<ProjectFormValues>(EMPTY_PROJECT_FORM);
  const [values, setValues] = useState<ProjectFormValues>(EMPTY_PROJECT_FORM);
  const [localErrors, setLocalErrors] = useState<ProjectFormErrors>({});
  const [saving, setSaving] = useState(false);

  const changes = changedFields(initial, values);
  const dirty = Object.keys(changes).length > 0;
  useUnsavedChangesGuard(dirty && !saving);

  // Prefill from the /planning/me project list; if it has no details (older
  // server, failed request) fall back to what the project list knows and say
  // so. Only changed fields are ever sent, so an unknown field stays as is.
  useEffect(() => {
    if (!projectId || !workspace || !mayEdit) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setPartialLoad(false);
    setLocalErrors({});
    fieldErrors.clear();
    const fromList = toFormValues({ name: workspace.project_name, event_date: workspace.project_date });
    Promise.all([
      fetchProjectDetails().catch(() => null),
      loadClients('', 'active').catch(() => [] as Client[]),
    ]).then(([details, list]) => {
      if (cancelled) return;
      const detail = details?.find(p => p.project_id === projectId);
      const base = detail
        ? toFormValues({ ...detail, name: workspace.project_name, event_date: detail.event_date ?? workspace.project_date })
        : fromList;
      setPartialLoad(!detail);
      setInitial(base);
      setValues(base);
      setClientName(detail?.client_name ?? '');
      setClients(list);
      setLoading(false);
    });
    return () => { cancelled = true; };
    // Re-run for another project or once the project list has arrived, not when it refreshes after a save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, !!workspace, mayEdit]);

  const errorFor = (key: keyof ProjectFormErrors) => localErrors[key] ?? fieldErrors.errorFor(key);

  const handleChange = useCallback((patch: Partial<ProjectFormValues>, ...cleared: (keyof ProjectFormErrors)[]) => {
    setValues(v => ({ ...v, ...patch }));
    cleared.forEach(k => {
      fieldErrors.clear(k);
      setLocalErrors(e => { if (!(k in e)) return e; const { [k]: _gone, ...rest } = e; return rest; });
    });
  }, [fieldErrors]);

  const leave = () => {
    if (dirty && !window.confirm(UNSAVED_MESSAGE)) return;
    navigate(LIST_PATH);
  };

  const handleSave = async () => {
    if (!projectId || savingRef.current || loading) return;
    const problems = validateProjectForm(values);
    setLocalErrors(problems);
    if (Object.keys(problems).length > 0) return;
    if (!dirty) { navigate(LIST_PATH); return; }
    savingRef.current = true;
    setSaving(true);
    fieldErrors.clear();
    try {
      const saved = await updateProject(projectId, changes);
      const next = toFormValues(saved);
      setInitial(next);
      setValues(next);
      setClientName(saved.client_name ?? '');
      showSuccess('Project updated.');
      // The list and header read from the project list, so re-read it.
      await refreshWorkspaces().catch(() => { /* the saved change stands; the list refreshes on the next load */ });
      navigate(LIST_PATH);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, "Couldn't save the project. Please try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!projectId || !workspace) {
    return <Notice icon="folder" title="Project not found" description="This project does not exist, or it is not in your account." />;
  }
  if (!mayEdit) {
    return <Notice icon="lock" title="You can't edit this project" description="Your role on this project can view it but not change it. Ask an owner or admin." />;
  }

  const title = initial.name || workspace.project_name || 'Project';
  const onBilling = tab === 'billing';

  return (
    <AccountFormPage
      view="project-edit"
      leaf={title}
      title={title}
      subtitle={onBilling ? 'Billing for this project.' : 'Details, client and dates.'}
      onSave={() => { void handleSave(); }}
      onCancel={leave}
      canSave={!loading && !saving && dirty}
      saving={saving}
      readOnly={onBilling}
    >
      <nav className="epp-tabs" aria-label="Project sections" {...{ [GUARD_SAFE_ATTR]: '' }}>
        {TABS.map(t => (
          <Link
            key={t.id}
            className={`epp-tab${tab === t.id ? ' epp-tab--active' : ''}`}
            to={t.id === 'details' ? editProjectPath(projectId) : projectBillingPath(projectId)}
            aria-current={tab === t.id ? 'page' : undefined}
            replace
          >
            {t.label}
          </Link>
        ))}
      </nav>

      {loading && <LoadingSpinner compact message="Loading project details…" />}

      {/* Both tabs stay mounted so unsaved Details are not lost on a visit to Billing. Save and Cancel are in the bar above. */}
      <form
        className="epp-form"
        hidden={loading || tab !== 'details'}
        noValidate
        aria-label="Project details"
        onSubmit={e => { e.preventDefault(); void handleSave(); }}
      >
        {partialLoad && (
          <p className="ap-note" role="status">Some details could not be loaded. Fields you leave alone will not change.</p>
        )}
        <section className="epp-card" aria-label="Project details">
          <ProjectDetailsForm values={values} errorFor={errorFor} onChange={handleChange} clients={clients} clientName={clientName} />
        </section>
      </form>
      {!loading && onBilling && (
        <section className="epp-card" aria-label="Project billing">
          <ProjectBillingPanel projectId={projectId} clientId={initial.clientId || null} />
        </section>
      )}
    </AccountFormPage>
  );
};

export default EditProjectPage;
